<template>
  <!-- a body-ba tesszuk: nyomtatasnal normal folyamban marad es tobb
       oldalra tordelodik -->
  <Teleport to="body">
    <div class="print-sheet ops-print">
      <!-- fejléc: egyszer, a lap tetején -->
      <header class="ops-head">
        <img v-if="image" :src="image" :alt="serial" class="ops-shot" />
        <div class="ops-id">
          <div class="ops-serial">{{ serial || "–" }}</div>
          <div class="ops-name">{{ name }}</div>
        </div>
        <div class="ops-facts">
          <div class="ops-fact">
            <span>{{ $t("operationBoard.printSteps") }}</span>
            <b>{{ stepCount }}</b>
          </div>
          <div class="ops-fact">
            <span>{{ $t("operationBoard.sumTime") }}</span>
            <b>{{ minutes(sumTime) }}</b>
          </div>
          <div class="ops-fact">
            <span>{{ $t("operationBoard.realTime") }}</span>
            <b>{{ minutes(realTime) }}</b>
          </div>
          <div class="ops-fact">
            <span>{{ $t("operationBoard.phases") }}</span>
            <b>{{ phases.length }}</b>
          </div>
        </div>
        <div class="ops-meta">
          <div>{{ today }}</div>
          <div class="ops-brand">Prodyflow</div>
        </div>
      </header>

      <!-- fázisonként; nyomtatásban két hasábra folyik -->
      <div class="ops-body">
        <section
          v-for="(ph, pi) in phases"
          :key="'pp' + pi"
          class="ops-phase"
          :class="{ 'is-par': ph.threads.length > 1 }"
        >
          <div class="ops-phase-bar">
            <span class="ops-phase-no">{{ pi + 1 }}</span>
            <span class="ops-phase-title">
              {{ $t("operationBoard.phase") }}
              <template v-if="ph.threads.length > 1">
                — {{ ph.threads.length }} {{ $t("operationBoard.atOnce") }}
              </template>
            </span>
            <i class="ops-phase-rule"></i>
            <span class="ops-phase-time">{{ ph.longest }} mp</span>
          </div>

          <div
            v-for="(th, ti) in ph.threads"
            :key="'pt' + pi + ti"
            class="ops-thread"
            :class="{ 'is-group': th.steps.length > 1 }"
          >
            <div class="ops-group-head" v-if="th.steps.length > 1">
              {{ $t("operationBoard.group") }} {{ pi + 1 }} ·
              {{ th.steps.length }} {{ $t("operationBoard.step") }} ·
              {{ th.time }} mp
            </div>

            <article v-for="(s, si) in th.steps" :key="s.key" class="ops-step">
              <div class="ops-step-no">
                {{ th.steps.length > 1 ? pi + 1 + "/" + (si + 1) : pi + 1 }}
              </div>
              <div class="ops-step-body">
                <div class="ops-step-top">
                  <span class="ops-step-op">{{ opName(s) }}</span>
                  <span class="ops-step-desc" v-if="s.description">{{
                    s.description
                  }}</span>
                  <span class="ops-step-desc ops-step-desc--empty" v-else>{{
                    $t("operationBoard.noDescription")
                  }}</span>
                  <span class="ops-step-time">{{ s.time || 0 }} mp</span>
                  <i
                    class="ops-dot"
                    :class="{ 'is-none': !s.videos_count }"
                    :title="
                      s.videos_count
                        ? $t('operationBoard.keyVideo')
                        : $t('operationBoard.keyNoVideo')
                    "
                  ></i>
                </div>
                <p class="ops-step-note" v-if="s.extra_info">
                  <span>{{ $t("operationBoard.printNote") }}</span>
                  {{ s.extra_info }}
                </p>
              </div>
            </article>
          </div>
        </section>
      </div>
    </div>
  </Teleport>
</template>

<script>
/**
 * Nyomtatható műveleti lap. Csak nyomtatáskor látszik; a képernyőn rejtve
 * marad, hogy a nézetet ne zavarja.
 */
export default {
  name: "OperationPrintSheet",
  props: {
    phases: Array,
    operations: Object,
    serial: String,
    name: String,
    image: String,
    stepCount: Number,
    sumTime: Number,
    realTime: Number,
  },
  computed: {
    today() {
      const d = new Date();
      const p = (n) => String(n).padStart(2, "0");
      return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate());
    },
  },
  methods: {
    opName(step) {
      return (
        this.operations?.[step.manufacturing_operation_id] || step.name || "–"
      );
    },
    minutes(sec) {
      return (sec / 60).toFixed(1) + " " + this.$t("operationBoard.minute");
    },
  },
};
</script>

<style scoped>
/* képernyőn nem látszik — csak a nyomtatott lapon */
.ops-print {
  display: none;
}
</style>

<style>
@media print {
  /* csak a műveleti lap kerüljön papírra. A lap statikus marad —
     abszolút pozicionálva nem tördelődne több oldalra. */
  /* csak az éppen nyomtatandó lap marad — közös osztály, hogy a két
     nyomtatási nézet szabálya ne oltsa ki egymást */
  body > *:not(.print-sheet) {
    display: none !important;
  }
  html,
  body {
    height: auto !important;
    overflow: visible !important;
    background: #fff !important;
  }
  .ops-print {
    display: block !important;
    position: static !important;
    padding: 9mm 9mm 10mm;
    width: auto;
    background: #fff;
    color: #1c1b22;
    font-family: "Segoe UI", Arial, sans-serif;
    font-size: 8.5pt;
    line-height: 1.22;
  }

  /* fekvő A4: a szélesség két hasábra oszlik, így feleannyi lap kell */
  @page {
    size: A4 landscape;
    margin: 0;
  }

  /* ---------- fejléc ---------- */
  .ops-head {
    display: flex;
    align-items: center;
    gap: 4mm;
    padding-bottom: 2mm;
    margin-bottom: 2.5mm;
    border-bottom: 1pt solid #1c1b22;
  }
  .ops-shot {
    width: 13mm;
    height: 17mm;
    object-fit: cover;
    border: 0.5pt solid #bbb;
    border-radius: 2mm;
  }
  .ops-serial {
    font-size: 15pt;
    font-weight: 700;
    line-height: 1.05;
    color: #713ae8;
  }
  .ops-name {
    font-size: 9pt;
    color: #444;
    max-width: 70mm;
  }
  .ops-facts {
    margin-left: auto;
    display: flex;
    gap: 5mm;
  }
  .ops-fact {
    text-align: right;
  }
  .ops-fact span {
    display: block;
    font-size: 6.5pt;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #777;
  }
  .ops-fact b {
    font-size: 10pt;
    font-weight: 700;
  }
  .ops-meta {
    display: none;
  }

  /* ---------- fázis: csíkos elválasztó ---------- */
  .ops-phase {
    margin-bottom: 2mm;
    break-inside: avoid;
    page-break-inside: avoid;
    /* a hasáb tetején ne lógjon be félbevágott sáv */
    -webkit-column-break-inside: avoid;
  }
  .ops-phase-bar {
    display: flex;
    align-items: center;
    gap: 2mm;
    padding: 0.8mm 0 1mm;
    margin-bottom: 1.2mm;
    border-top: 1.8pt solid #1c1b22;
    border-bottom: 0.5pt solid #ddd;
  }
  .ops-phase.is-par .ops-phase-bar {
    border-top-color: #c2761b;
  }
  .ops-phase-no {
    font-size: 8pt;
    font-weight: 700;
    min-width: 4.2mm;
    height: 4.2mm;
    text-align: center;
    border: 0.8pt solid #1c1b22;
    border-radius: 50%;
    line-height: 3.7mm;
  }
  .ops-phase.is-par .ops-phase-no {
    border-color: #c2761b;
    color: #c2761b;
  }
  .ops-phase-title {
    font-size: 8pt;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
  }
  .ops-phase.is-par .ops-phase-title {
    color: #c2761b;
  }
  .ops-phase-rule {
    flex: 1;
    height: 0.5pt;
    background: #ddd;
    border-top: 0.5pt solid #ddd;
  }
  .ops-phase-time {
    font-size: 9pt;
    font-weight: 700;
  }

  /* ---------- csoport ---------- */
  .ops-thread {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .ops-thread.is-group {
    margin: 0 0 0.7mm;
    border: 0.5pt solid #ccc;
    border-radius: 1.5mm;
    padding: 0.8mm 1.5mm 0.5mm;
  }
  .ops-group-head {
    font-size: 6.5pt;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: #777;
    margin-bottom: 0.8mm;
  }

  /* ---------- lépés: kártya, mint a képernyőn ---------- */
  .ops-step {
    display: flex;
    gap: 2mm;
    align-items: flex-start;
    padding: 0.9mm 1.5mm;
    margin-bottom: 0.7mm;
    border: 0.5pt solid #ddd;
    border-radius: 2mm;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .ops-thread.is-group .ops-step {
    border: 0;
    border-bottom: 0.4pt solid #eee;
    border-radius: 0;
    margin-bottom: 0;
    padding-left: 0;
  }
  .ops-thread.is-group .ops-step:last-child {
    border-bottom: 0;
  }
  .ops-step-no {
    flex: none;
    width: 8mm;
    font-size: 7.5pt;
    font-weight: 700;
    text-align: center;
    color: #555;
    border: 0.6pt solid #ccc;
    border-radius: 3mm;
    padding: 0.6mm 0;
  }
  .ops-step-body {
    flex: 1;
    min-width: 0;
  }
  .ops-step-top {
    display: flex;
    align-items: baseline;
    gap: 3mm;
  }
  .ops-step-op {
    flex: none;
    min-width: 30mm;
    font-size: 8.5pt;
    font-weight: 700;
  }
  .ops-step-time {
    margin-left: auto;
    font-size: 8pt;
    font-weight: 700;
    white-space: nowrap;
    border: 0.5pt solid #ccc;
    border-radius: 1.5mm;
    padding: 0.2mm 1.3mm;
  }
  /* a pötty kerettel készül, hogy háttérgrafika nélkül is látszódjon */
  .ops-dot {
    flex: none;
    width: 0;
    height: 0;
    border: 1.4mm solid #1a7f52;
    border-radius: 50%;
  }
  .ops-dot.is-none {
    border: 0.5pt solid #bbb;
    width: 2.3mm;
    height: 2.3mm;
  }
  /* a leírás a név mellett fut, így kitölti a fekvő lap szélességét */
  .ops-step-desc {
    flex: 1;
    min-width: 0;
    font-size: 8pt;
    color: #333;
  }
  .ops-step-desc--empty {
    color: #aaa;
    font-style: italic;
  }
  /* a megjegyzés saját sorban, behúzva — nem ragad a leíráshoz */
  .ops-step-note {
    margin: 0.4mm 0 0 10mm;
    padding-left: 2mm;
    border-left: 0.8pt solid #c2761b;
    font-size: 7.5pt;
    color: #444;
  }
  .ops-step-note span {
    font-weight: 700;
    text-transform: uppercase;
    font-size: 6pt;
    letter-spacing: 0.07em;
    color: #c2761b;
    margin-right: 1.2mm;
  }
}
</style>
